import {
  type OasOptionPayload, type OasStrikeSeriesPayload, type OptionAnalytics, type StrikeSeries, type VolCurve, type OptionExpiration, type OptionStrike, type UnderlyingRoot,
} from '../options';
import {type AccountItem} from '../account';
import {
  type AlgoDateRange,
  type AlgoDefinition,
  type AlgoRunReportUpdateDetails,
} from '../algo';
import {
  type BasketValue, type BasketValueRequest, type FutureSubscriptionResponse, type InstrumentDataSubscriptionRequestSymbol, type InstrumentDataSubscriptionResponse, type L2DataSubscriptionResponse,
  type SymbolLookupRequest, type TimeSeriesLookupRequest, type TimeSeriesLookupResponse,
} from '../marketData';
import {
  type CancelOrderRequest, type Direction, type OrderManagerSubscriptionUpdate, type OrderModifyRequest, type OrderRecord, type OrderRequest,
} from '../order';
import {
  type ContractData, type ExchangeSymbolIdentity, type OrderSide, type Symbol,
} from '../common';
import {type DefaultUpdateResponse, type ProductQtyDefault, type TradableSymbol} from '../defaults';
import {type InventoryRecord, type InventorySubscriptionUpdate} from '../inventory';
import {type AlgoMonitorReport, type AlgoSubscriptionUpdate, type SingleAlgoFullReport} from '../algo/fe-algo';
import {type TraderDBAccount} from '../traderdb';
import {type BaseApi, type SubscriptionManagerApi} from './base-api';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- API schemas explicitly distinguish JSON null from omitted fields.
type NullValue = null;

type SystemNotificationAction = {
  type: string;
  payload: unknown;
};

type AlgoStatusResponse = {
  algoRunId: string;
  status: string;
  statusDetails?: string | NullValue;
};

type AlgoChangeResponse = {
  algoRunStatus: AlgoStatusResponse;
  clientCorrelationId: {correlationId: string};
  endTimeUtc?: string | Date | NullValue;
  responseTimeUtc: string | Date;
  startTimeUtc?: string | Date | NullValue;
};

/** Date range accepted by the algo monitoring chart endpoint. */
export type AlgoMonitoringChartDataRequest = {
  algoRunId: string;
  endDate?: string | NullValue;
  startDate?: string | NullValue;
};

/** One normalized point returned by the algo monitoring chart endpoint. */
export type AlgoMonitoringChartDataPoint = {
  algo_run_id: string;
  longPercent?: number | string | NullValue;
  ref_time_utc: string;
  shortPercent?: number | string | NullValue;
  target_delta_long?: number | string | NullValue;
  target_delta_short?: number | string | NullValue;
  traded_delta_long?: number | string | NullValue;
  traded_delta_short?: number | string | NullValue;
  value_mid?: number | string | NullValue;
};

type AlgoRunResponse = AlgoChangeResponse & {
  endTimeUtc: string | Date;
  startTimeUtc: string | Date;
};

type AlgoResumeResponse = Omit<AlgoChangeResponse, 'algoRunStatus' | 'endTimeUtc' | 'startTimeUtc'> & {
  algoRunStatus: {
    algoRunId?: string | NullValue;
    status?: string | NullValue;
    statusDetails?: string | NullValue;
  };
  endTimeUtc?: string | Date;
  startTimeUtc?: string | Date;
};

type AlgoAbortResponse = AlgoChangeResponse | {message: string};

type AlgoAccountInfo = {
  clearingAccount?: string | NullValue;
  customText1: string | NullValue;
  customText2: string | NullValue;
  customText3: string | NullValue;
  deliverToCompId?: string;
  execCode?: string | NullValue;
  executionCode?: string | NullValue;
  executionTag?: string | NullValue;
  externalAccountCode?: string | NullValue;
  internalAccountCode?: string | NullValue;
  internalERPAccount?: string | NullValue;
  memo: string | NullValue;
  neovestAccountCode?: string | NullValue;
  traderId?: string | NullValue;
};

type AlgoRunRequestBody = {
  additionalParameters: Array<{
    algoParameterId: string;
    doubleValue?: string | number | NullValue;
    intValue?: number | NullValue;
    stringValue?: string | NullValue;
  }>;
  algoTradeRequests: Array<{
    accountInfo: AlgoAccountInfo;
    side: string | number;
    symbolIdentity: {
      callPut?: string;
      contractYYYYMM?: string;
      exchangeSymbol?: string;
      rootSymbol?: string;
      secType?: string;
      segmentMic: string;
      strikePrice?: string | number;
      symbol: string;
      underlyingRootSymbol?: string;
    };
    targetQuantity: string | number;
  }>;
  algoType: string;
  baseCurrencyCodeIso: string;
  durationInSeconds?: string | number | NullValue;
  endTimeRequestedUtc?: string | Date | NullValue;
  launchImmediately: boolean;
  removeParameters?: Array<{
    algoParameterId: string;
    doubleValue?: string | number | NullValue;
    intValue?: number | NullValue;
    stringValue?: string | NullValue;
  }>;
  startTimeRequestedUtc?: string | Date | NullValue;
};

export type RuntimeAlgoRunRequest = {
  accountInfo: AlgoAccountInfo;
  algoRunRequest: AlgoRunRequestBody;
};

export type RuntimeAlgoModifyRequest = RuntimeAlgoRunRequest & {algoRunId: string};

/** Order Manager wire payloads use JSON null for absent grid state and row counts. */
type Nullable<T> = T | NullValue;

export type ApiServicesType = {
  account: AccountApi;
  algo: AlgoApi;
  auth: AuthApi;
  defaults: DefaultsApi;
  inventory: InventoryApi;
  marketdata: MarketDataApi;
  mdsSubscriptionManager: SubscriptionManagerApi;
  notifications: NotificationsApi;
  oasSubscriptionManager: SubscriptionManagerApi;
  options: OptionsApi;
  orderSummary: OrderSummaryApi;
  ordermanager: OrderManagerApi;
  subscriptionManager: SubscriptionManagerApi;
  init: () => void;
  unsubscribeAndCleanup: () => void;
  onLoad: () => void;
};

export type AccountApi = {
  getUserAccounts: () => Promise<AccountItem[]>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  getUserAccountsFromTraderDB: () => Promise<TraderDBAccount[]>;
} & BaseApi;

export type AlgoApi = {
  abortAlgo: (algoId: string) => Promise<AlgoAbortResponse>;
  fetchAlgoDetails: (algoId: string) => Promise<SingleAlgoFullReport | undefined>;
  fetchAlgosList: (algoDateRange: AlgoDateRange, filters?: Record<string, string>) => Promise<AlgoMonitorReport[]>;
  getAlgoDefinition: () => Promise<AlgoDefinition[]>;
  getAlgoList: () => Promise<AlgoDefinition[]>;
  getAlgoMonitoringChartData: (parameters: AlgoMonitoringChartDataRequest, retryable?: boolean) => Promise<AlgoMonitoringChartDataPoint[]>;
  modifyAlgo: (parameters: RuntimeAlgoModifyRequest) => Promise<AlgoChangeResponse>;
  pauseAlgo: (algoId: string) => Promise<AlgoChangeResponse>;
  resumeAlgo: (algoId: string) => Promise<AlgoResumeResponse>;
  runAlgo: (parameters: RuntimeAlgoRunRequest) => Promise<AlgoRunResponse>;
  subscribeToAlgo: (algoId: string, cb: (error: unknown, algoUpdate?: AlgoSubscriptionUpdate) => unknown) => Promise<() => void>;
  subscribeToAllAlgo: (cb: (error: unknown, algoUpdate?: AlgoSubscriptionUpdate) => unknown) => () => void;
  subscribeToUserAlgo: (algoRunId: string, cb: (error: unknown, algoUpdate: AlgoRunReportUpdateDetails) => unknown) => Promise<() => void>;
  /**
   * @deprecated - unused
  */
  getCumulativeSavings: (parameters: AlgoDateRange, filters?: Record<string, string>) => Promise<unknown>;
  /**
   * @deprecated - unused
  */
  getTradedQuantities: (parameters: AlgoDateRange, filters?: Record<string, string>) => Promise<unknown>;
} & BaseApi;

export type UserAccessResponse = {
  algo: boolean;
  futures: boolean;
  options: boolean;
  viewData: boolean;
};
export type AuthApi = {
  logout: () => Promise<() => undefined>;
  userAccess: () => Promise<UserAccessResponse>;
  /**
    * @deprecated - unused
    */
  mockUser: (email: string) => Promise<unknown>;
  /**
   * @deprecated - unused
  */
  recordUserSpecs: (parameters: unknown) => Promise<unknown>;
  /**
    * @deprecated - unused
    */
  subscribeToAuthError: (callback: (error?: Error, update?: unknown) => unknown) => void;
} & BaseApi;

export type DefaultsApi = {
  getDefaults: () => Promise<TradableSymbol[]>;
  setDefaults: (products: ProductQtyDefault[]) => Promise<DefaultUpdateResponse>;
} & BaseApi;

export type InventoryApi = {
  subscribeToInventory: (cb: (error: unknown, update?: InventorySubscriptionUpdate, ...additionalArguments: unknown[]) => unknown) => () => void;
  /**
   * @deprecated - unused
   */
  getInventory: () => Promise<InventoryRecord[]>;
} & BaseApi;

type MarketDataSymbolIdentity = {
  exchSymbol?: string | NullValue;
  qepSymbol?: string | NullValue;
  segmentMIC?: string | NullValue;
};

export type MarketDataApi = {
  getContractData: (symbolKeys: ExchangeSymbolIdentity[]) => Promise<ContractData[]>;
  listExchanges: () => Promise<string[]>;
  mdsManager: SubscriptionManagerApi;
  subscribeToInstrumentData: (
    parameters: InstrumentDataSubscriptionRequestSymbol | InstrumentDataSubscriptionRequestSymbol[],
    cb: (error: unknown, update: InstrumentDataSubscriptionResponse | undefined) => unknown,
  ) => Array<() => void>;
  subscribeToL2Data: (symbolIdentity: MarketDataSymbolIdentity, cb: (error: unknown, quote?: L2DataSubscriptionResponse) => void) => () => void;
  symbolLookup: (parameters: SymbolLookupRequest) => Promise<Symbol[]>;
  symbolMysqlLookup: (symbol: string | Symbol, signal?: AbortSignal) => Promise<Symbol[]>;
  timeSeriesLookup: (parameters: TimeSeriesLookupRequest) => Promise<TimeSeriesLookupResponse>;
  /**
   * @deprecated
  */
  closeAllSubscriptions: () => Promise<void>;
  /**
   * @deprecated
  */
  getBasketValue: (parameters: BasketValueRequest) => Promise<BasketValue>;
  /**
   * @deprecated
  */
  subscribeToS2F: (parameters: {symbol: Symbol; quantity: number; direction: Direction}, cb: (error: Error | undefined, data: FutureSubscriptionResponse) => unknown) => () => void;
} & BaseApi;

export type NotificationsApi = {
  subscribeToSystemNotifications: (cb: (action: SystemNotificationAction) => void) => (() => void);
};

export type OptionsApi = {
  getExpirations: (exp: UnderlyingRoot, signal?: AbortSignal) => Promise<OptionExpiration[]>;
  getStrikes: (root: Required<OptionExpiration>, signal?: AbortSignal) => Promise<OptionStrike[]>;
  getUnderlyingRoots: (query: string, signal?: AbortSignal) => Promise<UnderlyingRoot[]>;
  subscribeToOptionsAnalyticsData: (parameters: OasOptionPayload, cb: (args: OptionAnalytics) => unknown) => () => void;
  subscribeToStrikeSeriesAnalyticsData: (parameters: OasStrikeSeriesPayload, cb: (args: StrikeSeries) => unknown) => () => void;
  subscribeToVolCurveData: (parameters: OasStrikeSeriesPayload, cb: (args: VolCurve) => unknown) => () => void;
} & BaseApi;

export type OrderSummaryApi = {
  subscribeToOrderSummary: (cb: (error: unknown, parameters?: OrderSummarySubscriptionUpdate) => unknown, detailedSource?: string) => () => void;

  /**
   * @deprecated
  */
  getOrderSummary?: () => Promise<OrderRecord[]>;
} & BaseApi;

/** Validated initial or incremental order-summary event delivered by the preload subscription. */
export type OrderSummarySubscriptionUpdate =
  | {topic: string; payload: Array<Record<string, unknown>>}
  | {topic: string; payload: Record<string, unknown>};

/** Serializable AG Grid column metadata accepted by Order Manager list requests. */
export type OrderManagerGridColumn = {
  aggFunc?: string;
  displayName?: string;
  field?: string;
  id?: string;
};

/** Serializable sort instruction accepted by Order Manager list requests. */
export type OrderManagerSortModelItem = {
  colId?: string;
  sort?: string;
};

/** Server-side grid request shared by execution-report and cancel-all operations. */
export type OrderManagerGridRequest = {
  endRow: number | string | undefined;
  filterModel: Nullable<Record<string, unknown>>;
  groupKeys: unknown[];
  pivotCols: unknown[];
  pivotMode: boolean;
  rowGroupCols: unknown[];
  sortModel: Nullable<OrderManagerSortModelItem[]>;
  startRow: number | string | undefined;
  valueCols: OrderManagerGridColumn[];
};

/** Execution-report page returned by the Order Manager server-side grid endpoint. */
export type GetExecutionReportsResponse = {
  orders: OrderRecord[];
  rowCount: Nullable<number>;
};

/** Bulk-cancel request for either selected order ids or the current server-side grid query. */
export type GetOrdersToCancelRequest =
  | {
    getOrdersToCancel: false;
    orders: Array<number | string>;
  }
  | OrderManagerGridRequest & {
    getOrdersToCancel: true;
  };

/** Count returned after Order Manager accepts one bulk-cancel request. */
export type GetOrdersToCancelResponse = {count: number};

export type OrderManagerApi = {
  cancelManyOrders: (parameters: {side: OrderSide | undefined; symbol: Symbol}) => Promise<OrderRecord[]>;
  cancelOrder: (order: CancelOrderRequest) => Promise<OrderRecord>;
  exportBlotterCsv: (params: any) => Promise<void>;
  getExecutionReports: (request: OrderManagerGridRequest) => Promise<GetExecutionReportsResponse>;
  getOrder: (orderId: string) => Promise<OrderRecord>;
  getOrderHistory: (parameters: {orderId: string}) => Promise<OrderRecord[]>;
  getColumnValues: (parameters: {columnNames: string[]; filterData: any}) => Promise<any[]>;
  getOrdersToCancel: (parameters: GetOrdersToCancelRequest) => Promise<GetOrdersToCancelResponse>;
  modifyOrder: (parameters: OrderModifyRequest) => Promise<OrderRecord>;
  placeOrder: (orderRequest: OrderRequest) => Promise<OrderRecord>;
  subscribeToExecutionReports: (cb: (error: unknown, executionReport?: OrderManagerSubscriptionUpdate) => unknown) => Promise<() => void>;
  subscribeToOrderHistoryReports: (orderId: string, cb: (error: unknown, executionReport?: OrderManagerSubscriptionUpdate) => unknown) => () => void;
  /**
   * @deprecated - unused
  */
  cancelMultipleOrders: () => Promise<void>;
  /**
  * @deprecated - unused
  */
  calculateMarketImpact: (parameters: {legs: Array<{symbol: Symbol; quantity: number; direction: Direction}>}) => Promise<unknown>;
  /**
  * @deprecated - unused
  */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  calculateManualTCA: (parameters: {legs: Array<{symbol: Symbol; quantity: number; direction: Direction}>}) => Promise<unknown>;
  /**
  * @deprecated - unused
 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  calculateAlgoTCA: (parameters: {legs: Array<{symbol: Symbol; quantity: number; direction: Direction}>}) => Promise<unknown>;
  /**
 * @deprecated - unused
 */
  unsubscribeToExecutionReports: () => void;
} & BaseApi;
